import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { changePassword } from '../services/auth';
import { getErrorMessage } from '../lib/api';

export default function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('De nye adgangskoder matcher ikke');
      return;
    }

    // Validate password requirements
    if (newPassword.length < 8) {
      setError('Adgangskode skal være mindst 8 tegn');
      return;
    }

    if (!/[a-zA-Z]/.test(newPassword)) {
      setError('Adgangskode skal indeholde mindst ét bogstav');
      return;
    }

    if (!/\d/.test(newPassword)) {
      setError('Adgangskode skal indeholde mindst ét tal');
      return;
    }

    setIsLoading(true);

    try {
      await changePassword({
        currentPassword: user?.mustChangePassword ? undefined : currentPassword,
        newPassword,
      });

      // If this was a forced password change, redirect to home
      if (user?.mustChangePassword) {
        // Log out and back in to refresh user data
        logout();
        navigate('/login', { 
          state: { message: 'Adgangskode ændret succesfuldt. Log venligst ind igen.' }
        });
      } else {
        navigate('/profile/' + user?.id, {
          state: { message: 'Adgangskode ændret succesfuldt' }
        });
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8 p-6">
      <div className="card">
        <h1 className="text-h2 font-heading font-bold mb-2">
          {user?.mustChangePassword ? 'Skift midlertidig adgangskode' : 'Skift adgangskode'}
        </h1>
        
        {user?.mustChangePassword && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-400 rounded-lg">
            <p className="text-sm text-gray-700">
              Du skal ændre din midlertidige adgangskode før du kan fortsætte.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!user?.mustChangePassword && (
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Nuværende adgangskode
              </label>
              <input
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="input"
                placeholder="••••••••"
              />
            </div>
          )}

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Ny adgangskode
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="input"
              placeholder="Mindst 8 tegn, 1 bogstav, 1 tal"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Bekræft ny adgangskode
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="input"
              placeholder="Gentag ny adgangskode"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-400 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Ændrer...' : 'Skift adgangskode'}
          </button>
        </form>
      </div>
    </div>
  );
}
